import React, { useState, useEffect, useLayoutEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Skeleton,
  CircularProgress,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  InfoOutlined,
  CheckCircle,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useLazyGetMeQuery, useUpdateProfileMutation, useChangePasswordMutation } from "Services/user";
import { useSignoutMutation } from "Services/auth";
import { securityClient } from "securityClient";
import { useAppDispatch, useAppSelector } from "State/hooks";
import { clearCompany } from "State/companySlice";
import { useGetPipelinesQuery } from "Services/pipeline";
import { useGetSubscriptionStatusQuery, useCreatePortalSessionMutation } from "Services/subscription";
import { CurrentUserResponse } from "Models/auth";

// ---------- Styled components ----------

const PageContainer = styled(Box)({
  maxWidth: 800,
  margin: "0 auto",
  padding: "32px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 24,
});

const SectionCard = styled(Paper)({
  padding: 28,
  borderRadius: 12,
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
});

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: 18,
  marginBottom: 20,
});

const FieldLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: 14,
  marginBottom: 2,
  color: "#555",
});

const FieldValue = styled(Typography)({
  fontSize: 15,
  color: "#222",
  marginBottom: 16,
});

const StyledTextField = styled(TextField)({
  marginBottom: 16,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
});

const PrimaryButton = styled(Button)({
  background: "linear-gradient(90deg, #2A7BFF 0%, #3F8CFF 100%)",
  color: "white",
  padding: "10px 24px",
  borderRadius: 8,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  "&:hover": {
    background: "linear-gradient(90deg, #1D63E8 0%, #2A7BFF 100%)",
  },
  "&:disabled": {
    background: "#ccc",
    color: "#fff",
  },
});

const OutlineButton = styled(Button)({
  borderRadius: 8,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  borderColor: "#ddd",
});

const DangerButton = styled(Button)({
  borderRadius: 8,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  color: "#d32f2f",
  borderColor: "#d32f2f",
  "&:hover": {
    backgroundColor: "rgba(211,47,47,0.05)",
    borderColor: "#d32f2f",
  },
});

const RequirementRow = ({
  met,
  label,
}: {
  met: boolean;
  label: string;
}) => (
  <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
    {met ? (
      <CheckCircle sx={{ fontSize: 15, color: "#2e7d32" }} />
    ) : (
      <RadioButtonUnchecked sx={{ fontSize: 15, color: "#bbb" }} />
    )}
    <Typography variant="caption" color={met ? "#2e7d32" : "text.secondary"}>
      {label}
    </Typography>
  </Box>
);

// ---------- Helpers ----------

const formatDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const validatePasswordStrength = (password: string) => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumberOrSpecial: /[0-9!@#$%^&*(),.?":{}|<>]/.test(password),
});

// ---------- Profile cache ----------
// Stores last-saved values so Cognito's mid-session cache doesn't show stale data.

const PROFILE_CACHE_KEY = "userProfile";

interface ProfileCache {
  firstName: string;
  lastName: string;
  jobTitle?: string; // undefined = written from login only; string = complete
  defaultPipeline?: string;
}

const readProfileCache = (): ProfileCache | null => {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    return raw ? (JSON.parse(raw) as ProfileCache) : null;
  } catch {
    return null;
  }
};

/**
 * Both caches are only meaningful for a signed-in user, so a write with no token is
 * always a late write. Sign-out clears storage and then navigates client-side, which
 * leaves this component mounted long enough for an in-flight `loadUser` to write the
 * profile cache back *after* the clear — resurrecting "userProfile" in an otherwise
 * empty localStorage. Dropping tokenless writes closes that race whatever the timing.
 */
const hasSession = (): boolean => !!localStorage.getItem("idToken");

const writeProfileCache = (data: ProfileCache): void => {
  try {
    if (!hasSession()) return;
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(data));
  } catch {}
};

// Draft cache — persists unsaved edits across navigation.
// initialFormData always reflects the last SAVED state.

const PROFILE_DRAFT_KEY = "userProfileDraft";

const readProfileDraft = (): { firstName: string; lastName: string; jobTitle: string; defaultPipeline?: string } | null => {
  try {
    const raw = localStorage.getItem(PROFILE_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeProfileDraft = (data: { firstName: string; lastName: string; jobTitle: string; defaultPipeline?: string }): void => {
  try {
    if (!hasSession()) return;
    localStorage.setItem(PROFILE_DRAFT_KEY, JSON.stringify(data));
  } catch {}
};

const clearProfileDraft = (): void => {
  localStorage.removeItem(PROFILE_DRAFT_KEY);
};

// ---------- Component ----------

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const companyId = useAppSelector((state) => state.company.companyId);
  const { data: pipelines } = useGetPipelinesQuery(companyId, { skip: !companyId });
  const { data: subscription, isLoading: isSubscriptionLoading } = useGetSubscriptionStatusQuery(undefined, { skip: !companyId });
  const [createPortalSession, { isLoading: isOpeningPortal }] = useCreatePortalSessionMutation();

  const [fetchMe, { isLoading: isLoadingUserData }] = useLazyGetMeQuery();
  const [updateProfile, { isLoading: isSavingProfile }] = useUpdateProfileMutation();
  const [changePassword, { isLoading: isChangingPassword }] = useChangePasswordMutation();
  const [signout, { isLoading: isSigningOut }] = useSignoutMutation();

  // User data
  const [user, setUser] = useState<CurrentUserResponse | null>(null);
  const [formSeeded, setFormSeeded] = useState(false);

  // Profile form
  const [formData, setFormData] = useState({ firstName: "", lastName: "", jobTitle: "", defaultPipeline: "" });
  const [initialFormData, setInitialFormData] = useState({ firstName: "", lastName: "", jobTitle: "", defaultPipeline: "" });
  const [profileFieldErrors, setProfileFieldErrors] = useState<{
    firstName?: string;
    lastName?: string;
    jobTitle?: string;
  }>({});

  const isNewChange =
    formData.firstName !== initialFormData.firstName ||
    formData.lastName !== initialFormData.lastName ||
    formData.jobTitle !== initialFormData.jobTitle ||
    formData.defaultPipeline !== initialFormData.defaultPipeline;

  // Modal states
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const showToast = (message: string, severity: "success" | "error") => {
    setToast({ open: true, message, severity });
  };

  const handleSessionExpired = useCallback(() => {
    securityClient.clearToken();
    dispatch(clearCompany());
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  }, [dispatch, navigate]);

  // Load user data on mount
  useEffect(() => {
    const loadUser = async () => {
      const cached = readProfileCache();
      // Cache is "complete" once jobTitle is present (written by Settings or after save).
      // A cache written at login time only has firstName/lastName.
      const cacheIsComplete = cached !== null && cached.jobTitle !== undefined;

      if (cached) {
        const formFromCache = {
          firstName: cached.firstName,
          lastName: cached.lastName,
          jobTitle: cached.jobTitle ?? "",
          defaultPipeline: cached.defaultPipeline ?? "",
        };
        setFormData(formFromCache);
        setInitialFormData(formFromCache);
        if (cacheIsComplete) setFormSeeded(true);
      }

      // Restore unsaved edits the user left before navigating away.
      // initialFormData stays as the last SAVED state so isNewChange stays true.
      const draft = readProfileDraft();
      if (draft) {
        setFormData((prev) => ({ ...prev, ...draft }));
        if (cacheIsComplete) setFormSeeded(true);
      }

      try {
        const userData = await fetchMe().unwrap();
        setUser(userData);

        if (!cacheIsComplete) {
          // First visit after login: Cognito is fresh, and we need jobTitle
          // which isn't in the auth response. Prefer cached firstName/lastName
          // from login over API in case Cognito is already stale.
          const initial = {
            firstName: cached?.firstName ?? userData.firstName ?? "",
            lastName: cached?.lastName ?? userData.lastName ?? "",
            jobTitle: userData.jobTitle ?? "",
            defaultPipeline: "",
          };
          if (!draft) setFormData(initial);
          setInitialFormData(initial);
          writeProfileCache({ firstName: initial.firstName, lastName: initial.lastName, jobTitle: initial.jobTitle });
          setFormSeeded(true);
        }
        // defaultPipeline is always synced via the useEffect below once user is set.
      } catch (err: any) {
        if (err?.status === 401) {
          handleSessionExpired();
        } else {
          showToast("Failed to load user data. Please refresh the page.", "error");
        }
      }
    };
    loadUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync defaultPipeline from API into form state before the browser paints,
  // so the Select never renders with a blank value when pipelines are already cached.
  useLayoutEffect(() => {
    if (!user) return;
    const dp = user.defaultPipeline ?? "";
    if (!dp) return;
    // Cache is written on every save and is always more recent than the Cognito
    // token. Only fall back to the API value when the cache has nothing.
    setFormData((prev) => prev.defaultPipeline ? prev : { ...prev, defaultPipeline: dp });
    setInitialFormData((prev) => prev.defaultPipeline ? prev : { ...prev, defaultPipeline: dp });
  }, [user]);

  // ---------- Profile form handlers ----------

  const handleFormChange =
    (field: "firstName" | "lastName" | "jobTitle") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFormData = { ...formData, [field]: e.target.value };
      setFormData(newFormData);
      writeProfileDraft(newFormData);
      if (profileFieldErrors[field]) {
        setProfileFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const validateProfileForm = (): boolean => {
    const errors: { firstName?: string; lastName?: string; jobTitle?: string } = {};
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required.";
    } else if (formData.firstName.trim().length > 100) {
      errors.firstName = "First name must be 100 characters or less.";
    }
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required.";
    } else if (formData.lastName.trim().length > 100) {
      errors.lastName = "Last name must be 100 characters or less.";
    }
    if (formData.jobTitle.length > 150) {
      errors.jobTitle = "Job title must be 150 characters or less.";
    }
    setProfileFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateProfileForm()) return;

    const changedFields: { firstName?: string; lastName?: string; jobTitle?: string; defaultPipeline?: string } = {};
    if (formData.firstName.trim() !== initialFormData.firstName)
      changedFields.firstName = formData.firstName.trim();
    if (formData.lastName.trim() !== initialFormData.lastName)
      changedFields.lastName = formData.lastName.trim();
    if (formData.jobTitle !== initialFormData.jobTitle)
      changedFields.jobTitle = formData.jobTitle;
    if (formData.defaultPipeline !== initialFormData.defaultPipeline)
      changedFields.defaultPipeline = formData.defaultPipeline;

    try {
      const result = await updateProfile(changedFields).unwrap();
      const updated = result.user;
      setUser(updated);
      const newInitial = {
        firstName: updated.firstName ?? "",
        lastName: updated.lastName ?? "",
        jobTitle: updated.jobTitle ?? "",
        defaultPipeline: updated.defaultPipeline ?? "",
      };
      setFormData(newInitial);
      setInitialFormData(newInitial);
      writeProfileCache({ firstName: newInitial.firstName, lastName: newInitial.lastName, jobTitle: newInitial.jobTitle, defaultPipeline: newInitial.defaultPipeline });
      clearProfileDraft();
      showToast("Profile updated successfully", "success");
    } catch (err: any) {
      if (err?.status === 401) {
        handleSessionExpired();
      } else {
        const msg =
          err?.data?.message ||
          err?.message ||
          "Failed to update profile. Please try again.";
        showToast(msg, "error");
      }
    }
  };

  // ---------- Password modal handlers ----------

  const handlePasswordFormChange =
    (field: "currentPassword" | "newPassword" | "confirmPassword") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPasswordForm((prev) => ({ ...prev, [field]: e.target.value }));
      if (field === "currentPassword") setPasswordError(null);
      if (field === "newPassword" || field === "confirmPassword")
        setConfirmPasswordError(null);
    };

  const handleClosePasswordModal = () => {
    setIsChangePasswordModalOpen(false);
    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordError(null);
    setConfirmPasswordError(null);
    setShowPasswords({ current: false, new: false, confirm: false });
  };

  const passwordRequirements = validatePasswordStrength(passwordForm.newPassword);
  const passwordMeetsRequirements = Object.values(passwordRequirements).every(Boolean);

  const isPasswordFormValid =
    !!passwordForm.currentPassword &&
    passwordMeetsRequirements &&
    !!passwordForm.confirmPassword &&
    passwordForm.newPassword === passwordForm.confirmPassword;

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }
    if (!passwordMeetsRequirements) return;

    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      }).unwrap();
      handleClosePasswordModal();
      showToast("Password changed successfully", "success");
    } catch (err: any) {
      if (err?.status === 401) {
        setPasswordError("Current password is incorrect. Please try again.");
      } else if (err?.status === 400) {
        setPasswordError(
          err?.data?.message || "Password must meet all requirements listed above."
        );
      } else {
        setPasswordError("Something went wrong. Please try again later.");
      }
    }
  };

  // ---------- Subscription ----------

  const handleManageBilling = async () => {
    try {
      const { url } = await createPortalSession().unwrap();
      window.location.href = url;
    } catch (err: any) {
      if (err?.status === 401) {
        handleSessionExpired();
      } else {
        const msg =
          err?.data?.message ||
          err?.message ||
          "Failed to open billing portal. Please try again.";
        showToast(msg, "error");
      }
    }
  };

  // ---------- Sign out ----------

  const handleSignOut = async () => {
    try {
      await signout().unwrap();
    } catch (_) {
      // proceed with local logout even if API call fails
    }
    securityClient.clearToken();
    dispatch(clearCompany());
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  // ---------- Render ----------

  const isAccountInfoLoading = isLoadingUserData && !user;
  const isFormLoading = !formSeeded;

  return (
    <PageContainer>
      <Typography variant="h2" fontWeight={700} mb={1}>
        Settings
      </Typography>

      {/* Account Information */}
      <SectionCard>
        <SectionTitle>Account Information</SectionTitle>

        <FieldLabel>Email</FieldLabel>
        {isAccountInfoLoading ? (
          <Skeleton width={220} height={24} sx={{ mb: 2 }} />
        ) : (
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Typography fontSize={15}>{user?.email}</Typography>
            {user?.emailVerified && (
              <Typography fontSize={12} color="#2e7d32" fontWeight={600}>
                ✓ Verified
              </Typography>
            )}
          </Box>
        )}

        <FieldLabel>Member Since</FieldLabel>
        {isAccountInfoLoading ? (
          <Skeleton width={160} height={24} sx={{ mb: 2 }} />
        ) : (
          <FieldValue>{user?.createdAt ? formatDate(user.createdAt) : "—"}</FieldValue>
        )}

        <FieldLabel>
          <Box display="flex" alignItems="center" gap={0.5} component="span">
            Company ID
            <Tooltip title="Use this when contacting support" placement="right">
              <InfoOutlined sx={{ fontSize: 16, color: "#888", cursor: "pointer" }} />
            </Tooltip>
          </Box>
        </FieldLabel>
        {isAccountInfoLoading ? (
          <Skeleton width={240} height={24} sx={{ mb: 1 }} />
        ) : (
          <>
            <FieldValue sx={{ fontFamily: "monospace", mb: 0.5 }}>{user?.companyId}</FieldValue>
            <Typography fontSize={12} color="text.secondary" mb={2}>
              Use this when contacting support
            </Typography>
          </>
        )}

        <FieldLabel>Profile Last Updated</FieldLabel>
        {isAccountInfoLoading ? (
          <Skeleton width={160} height={24} />
        ) : (
          <Typography fontSize={15}>{user?.updatedAt ? formatDate(user.updatedAt) : "—"}</Typography>
        )}
      </SectionCard>

      {/* Profile Information */}
      <SectionCard>
        <SectionTitle>Profile Information</SectionTitle>

        {isFormLoading ? (
          <>
            <Skeleton height={56} sx={{ mb: 2 }} />
            <Skeleton height={56} sx={{ mb: 2 }} />
            <Skeleton height={56} sx={{ mb: 2 }} />
            <Skeleton height={56} sx={{ mb: 2 }} />
          </>
        ) : (
          <>
            <StyledTextField
              fullWidth
              label="First Name *"
              value={formData.firstName}
              onChange={handleFormChange("firstName")}
              error={!!profileFieldErrors.firstName}
              helperText={profileFieldErrors.firstName}
              disabled={isSavingProfile}
              inputProps={{ maxLength: 100 }}
            />
            <StyledTextField
              fullWidth
              label="Last Name *"
              value={formData.lastName}
              onChange={handleFormChange("lastName")}
              error={!!profileFieldErrors.lastName}
              helperText={profileFieldErrors.lastName}
              disabled={isSavingProfile}
              inputProps={{ maxLength: 100 }}
            />
            <StyledTextField
              fullWidth
              label="Job Title"
              value={formData.jobTitle}
              onChange={handleFormChange("jobTitle")}
              error={!!profileFieldErrors.jobTitle}
              helperText={profileFieldErrors.jobTitle || "e.g., Sales Manager, Director of Sales"}
              disabled={isSavingProfile}
              inputProps={{ maxLength: 150 }}
            />
            {!user || !pipelines ? (
              <Skeleton height={56} sx={{ mb: 2 }} />
            ) : (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Default Pipeline</InputLabel>
              <Select
                value={formData.defaultPipeline}
                label="Default Pipeline"
                onChange={(e) => {
                  const updated = { ...formData, defaultPipeline: e.target.value };
                  setFormData(updated);
                  writeProfileDraft(updated);
                }}
                disabled={isSavingProfile}
                sx={{ borderRadius: 2 }}
              >
                {pipelines.map((p) => (
                  <MenuItem key={p.sourcePipelineId} value={p.sourcePipelineId}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            )}
          </>
        )}

        <Box display="flex" justifyContent="flex-end" mt={1}>
          <PrimaryButton
            onClick={handleSaveProfile}
            disabled={!isNewChange || isSavingProfile || isFormLoading}
            startIcon={
              isSavingProfile ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {isSavingProfile ? "Saving..." : "Save Changes"}
          </PrimaryButton>
        </Box>
      </SectionCard>

      {/* Security */}
      <SectionCard>
        <SectionTitle>Security</SectionTitle>
        {localStorage.getItem("authProvider") === "google" ? (
          <Box>
            <FieldLabel>Password</FieldLabel>
            <Typography fontSize={14} color="text.secondary">
              Your account uses Google Sign-In. Password management is handled by Google and cannot be changed here.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <FieldLabel>Password</FieldLabel>
              <Typography fontSize={15} letterSpacing={4} color="text.secondary">
                ••••••••
              </Typography>
            </Box>
            <OutlineButton
              variant="outlined"
              onClick={() => setIsChangePasswordModalOpen(true)}
              disabled={isFormLoading}
            >
              Change Password
            </OutlineButton>
          </Box>
        )}
      </SectionCard>

      {/* Subscription */}
      <SectionCard>
        <SectionTitle>Subscription</SectionTitle>
        {isSubscriptionLoading ? (
          <>
            <Skeleton width={160} height={24} sx={{ mb: 2 }} />
            <Skeleton width={220} height={24} />
          </>
        ) : subscription?.plan === "trialing" || subscription?.plan === "expired" ? (
          <>
            <FieldLabel>Plan</FieldLabel>
            <FieldValue>
              {subscription.plan === "trialing"
                ? `Free Trial${subscription.trial_ends_at ? ` — ends ${formatDate(subscription.trial_ends_at)}` : ""}`
                : "Trial Expired"}
            </FieldValue>
            <PrimaryButton onClick={() => navigate("/subscribe")}>
              {subscription.plan === "trialing" ? "Upgrade" : "Subscribe"}
            </PrimaryButton>
          </>
        ) : (
          <>
            <FieldLabel>Plan</FieldLabel>
            <FieldValue sx={{ textTransform: "capitalize" }}>{subscription?.plan}</FieldValue>
            {subscription?.payment_status === "past_due" && (
              <Typography fontSize={13} color="#d32f2f" fontWeight={600} mb={2}>
                Your last payment failed. Please update your payment method.
              </Typography>
            )}
            {subscription?.cancel_at_period_end && subscription?.current_period_end && (
              <Typography fontSize={13} color="text.secondary" mb={2}>
                Your subscription will end on {formatDate(subscription.current_period_end)}
              </Typography>
            )}
            <OutlineButton variant="outlined" onClick={handleManageBilling} disabled={isOpeningPortal}>
              {isOpeningPortal ? "Loading..." : "Manage Billing"}
            </OutlineButton>
          </>
        )}
      </SectionCard>

      {/* Integrations */}
      <SectionCard
        sx={{ cursor: "pointer", "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.14)" } }}
        onClick={() => navigate("/integrations")}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <SectionTitle sx={{ mb: 0.5 }}>Integrations</SectionTitle>
            <Typography fontSize={14} color="text.secondary">
              Manage your HubSpot and Salesforce connections
            </Typography>
          </Box>
          <Typography fontSize={20} color="text.secondary">›</Typography>
        </Box>
      </SectionCard>

      {/* Account Actions */}
      <SectionCard>
        <SectionTitle>Account Actions</SectionTitle>
        <DangerButton
          variant="outlined"
          fullWidth
          onClick={() => setIsSignOutDialogOpen(true)}
        >
          Sign Out
        </DangerButton>
      </SectionCard>

      {/* Legal */}
      <SectionCard
        sx={{ cursor: "pointer", "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.14)" } }}
        onClick={() => navigate("/settings/terms")}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <SectionTitle sx={{ mb: 0.5 }}>Terms of Service</SectionTitle>
            <Typography fontSize={14} color="text.secondary">
              Review the terms governing your use of RevOptica
            </Typography>
          </Box>
          <Typography fontSize={20} color="text.secondary">›</Typography>
        </Box>
      </SectionCard>

      <SectionCard
        sx={{ cursor: "pointer", "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.14)" } }}
        onClick={() => navigate("/settings/privacy")}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <SectionTitle sx={{ mb: 0.5 }}>Privacy Policy</SectionTitle>
            <Typography fontSize={14} color="text.secondary">
              Learn how we collect, use, and protect your information
            </Typography>
          </Box>
          <Typography fontSize={20} color="text.secondary">›</Typography>
        </Box>
      </SectionCard>

      {/* Change Password Modal */}
      <Dialog
        open={isChangePasswordModalOpen}
        onClose={handleClosePasswordModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle fontWeight={700}>Change Password</DialogTitle>
        <DialogContent>
          {/* Current Password */}
          <StyledTextField
            fullWidth
            label="Current Password *"
            type={showPasswords.current ? "text" : "password"}
            value={passwordForm.currentPassword}
            onChange={handlePasswordFormChange("currentPassword")}
            error={!!passwordError}
            helperText={passwordError ?? undefined}
            disabled={isChangingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPasswords((prev) => ({ ...prev, current: !prev.current }))
                    }
                    edge="end"
                  >
                    {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1 }}
          />

          {/* New Password */}
          <StyledTextField
            fullWidth
            label="New Password *"
            type={showPasswords.new ? "text" : "password"}
            value={passwordForm.newPassword}
            onChange={handlePasswordFormChange("newPassword")}
            disabled={isChangingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPasswords((prev) => ({ ...prev, new: !prev.new }))
                    }
                    edge="end"
                  >
                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Password requirements */}
          <Box mb={2} pl={0.5}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
              Password requirements:
            </Typography>
            <RequirementRow met={passwordRequirements.minLength} label="At least 8 characters" />
            <RequirementRow met={passwordRequirements.hasUppercase} label="One uppercase letter (A–Z)" />
            <RequirementRow met={passwordRequirements.hasLowercase} label="One lowercase letter (a–z)" />
            <RequirementRow
              met={passwordRequirements.hasNumberOrSpecial}
              label="One number or special character"
            />
          </Box>

          {/* Confirm Password */}
          <StyledTextField
            fullWidth
            label="Confirm New Password *"
            type={showPasswords.confirm ? "text" : "password"}
            value={passwordForm.confirmPassword}
            onChange={handlePasswordFormChange("confirmPassword")}
            error={!!confirmPasswordError}
            helperText={confirmPasswordError ?? undefined}
            disabled={isChangingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPasswords((prev) => ({ ...prev, confirm: !prev.confirm }))
                    }
                    edge="end"
                  >
                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <OutlineButton variant="outlined" onClick={handleClosePasswordModal} disabled={isChangingPassword}>
            Cancel
          </OutlineButton>
          <PrimaryButton
            onClick={handleChangePassword}
            disabled={!isPasswordFormValid || isChangingPassword}
            startIcon={
              isChangingPassword ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {isChangingPassword ? "Changing Password..." : "Change Password"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Sign Out Confirmation Dialog */}
      <Dialog
        open={isSignOutDialogOpen}
        onClose={() => setIsSignOutDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle fontWeight={700}>Sign Out?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            You will be signed out from all devices and sessions.
          </Typography>
          <Typography color="text.secondary" mt={1}>
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <OutlineButton
            variant="outlined"
            onClick={() => setIsSignOutDialogOpen(false)}
            disabled={isSigningOut}
          >
            Cancel
          </OutlineButton>
          <Button
            variant="contained"
            color="error"
            onClick={handleSignOut}
            disabled={isSigningOut}
            startIcon={
              isSigningOut ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {isSigningOut ? "Signing Out..." : "Sign Out"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Settings;
